import { useState, type FormEvent } from 'react';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于牛牛影视',
  description: '牛牛影视是领先的免费高清影视聚合平台，致力于为用户提供电影大全、电视剧全集、综艺和动漫的免费在线观看服务。',
  about: {
    '@type': 'Organization',
    name: '牛牛影视',
    description: '免费高清影视聚合平台',
    foundingDate: '2020',
    areaServed: '全球中文用户',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '牛牛影视真的完全免费吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的！牛牛影视所有影视内容完全免费，无需注册会员，无需任何付费，真正做到观影零门槛。我们通过广告合作维持平台运营，用户只需享受免费高清的观影体验。',
      },
    },
    {
      '@type': 'Question',
      name: '牛牛影视的影视资源从哪里来？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '牛牛影视聚合了来自各大视频平台的公开影视资源，通过智能搜索和聚合技术，为用户提供一站式的影视内容入口。我们严格遵守相关法律法规，尊重知识产权。',
      },
    },
    {
      '@type': 'Question',
      name: '牛牛影视支持哪些设备观看？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '牛牛影视支持全平台观看，包括电脑、平板和手机。无论您使用Windows、Mac、iOS还是Android设备，都可以流畅访问牛牛影视，享受高清观影体验。',
      },
    },
    {
      '@type': 'Question',
      name: '为什么有些视频无法播放？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '由于牛牛影视聚合的是第三方影视资源，个别视频可能因为源站下架或网络波动导致暂时无法播放。如遇此类情况，建议尝试其他推荐资源或稍后再试。',
      },
    },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9ng17e8mbnljin5a020', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam') || msg.includes('spam') || msg.includes('垃圾')) {
          setFormStatus('success');
          form.reset();
          return;
        }
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOMeta
        title="关于我们 - 牛牛影视 | 免费高清影视聚合平台"
        description="牛牛影视是领先的免费高清影视聚合平台，覆盖电影大全、电视剧全集、综艺和动漫。了解我们的品牌故事、用户口碑和联系方式。"
        keywords="牛牛影视,关于我们,免费影视平台,品牌介绍,联系我们"
        canonicalUrl="https://niuniu.tv/about"
        schemaData={{ ...aboutSchema, ...faqSchema }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />

              <section className="mt-6">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                  关于牛牛影视
                </h1>
                <p className="text-sm text-foreground-500 mb-8">
                  免费高清影视聚合平台，让每个人都能享受纯粹的观影乐趣
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
                  <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                    <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                      <i className="ri-eye-line text-xl"></i>
                    </span>
                    <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">我们的愿景</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      让每一个人都能免费享受高品质的影视内容，打破付费壁垒，让好作品被更多人看到。
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                    <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                      <i className="ri-rocket-2-line text-xl"></i>
                    </span>
                    <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">我们的使命</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      聚合全网优质影视资源，提供流畅高清的观影体验，成为用户首选的一站式免费影视入口。
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                    <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                      <i className="ri-heart-line text-xl"></i>
                    </span>
                    <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">核心价值</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      完全免费、高清流畅、资源丰富、更新及时。用户至上，用真诚和品质赢得每一位用户的信赖。
                    </p>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">品牌故事</h2>
                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    牛牛影视诞生于2020年，创始团队由一群热爱影视的技术极客组成。在观察到市场上主流视频平台会员费用不断攀升、广告体验日益恶化的问题后，我们决定打造一个真正免费、纯净的影视聚合平台。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    我们深知，好的影视作品应该被更多人欣赏，而不应该被高昂的会员费用阻挡在外。牛牛影视通过智能搜索和聚合技术，将分散在网络各处的优质影视资源整合到一个平台上，让用户能够一站式找到想看的内容。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    经过多年的持续优化和迭代，牛牛影视已经从最初的小众工具成长为拥有数百万用户的影视平台。我们的内容库涵盖电影、电视剧、综艺、动漫等全品类，每日更新，保证用户总能找到新鲜好看的内容。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    未来，牛牛影视将继续坚持免费路线，不断优化产品体验，丰富内容资源，让更多用户能够轻松享受影视带来的快乐。感谢每一位用户的支持与陪伴！
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">用户口碑</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {[
                    {
                      name: '小王同学',
                      role: '资深影迷',
                      avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20young%20Asian%20man%2C%20friendly%20smile%2C%20simple%20white%20background%2C%20clean%20studio%20lighting&width=100&height=100&seq=avatar-01&orientation=squarish',
                      text: '用了牛牛影视快两年了，真心推荐！资源更新特别快，新上映的电影很快就能看到。画质也很清晰，关键是完全免费，省了一大笔会员费。',
                    },
                    {
                      name: '追剧小达人',
                      role: '追剧爱好者',
                      avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20young%20Asian%20woman%2C%20warm%20smile%2C%20simple%20white%20background%2C%20clean%20studio%20lighting&width=100&height=100&seq=avatar-02&orientation=squarish',
                      text: '作为重度追剧患者，牛牛影视就是我的快乐源泉！国产剧、韩剧应有尽有，更新超快。手机看也特别流畅，通勤路上再也不无聊了。',
                    },
                    {
                      name: '老张影视',
                      role: '影视博主',
                      avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20middle-aged%20Asian%20man%2C%20confident%20expression%2C%20simple%20white%20background%2C%20clean%20studio%20lighting&width=100&height=100&seq=avatar-03&orientation=squarish',
                      text: '做影视内容这么多年，牛牛影视是我最常给粉丝推荐的平台。全免费、不卡顿、资源全，这三个优点就足够秒杀大部分平台了。希望越做越好！',
                    },
                  ].map((review) => (
                    <div key={review.name} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={review.avatar}
                          alt={review.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <p className="text-sm font-semibold text-foreground-950">{review.name}</p>
                          <p className="text-xs text-foreground-500">{review.role}</p>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">常见问题</h2>
                <div className="space-y-3">
                  {faqSchema.mainEntity.map((faq, idx) => (
                    <details key={idx} className="group bg-background-100 rounded-xl overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <span className="text-sm font-medium text-foreground-800">{faq.name}</span>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.acceptedAnswer.text}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">联系我们</h2>
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="flex-1 bg-background-100 rounded-xl p-6 border border-background-200/70">
                    <h3 className="text-base font-bold text-foreground-950 mb-1 font-heading">联系方式</h3>
                    <p className="text-xs text-foreground-500 mb-5">有任何问题或建议，欢迎随时联系我们</p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                          <i className="ri-mail-line"></i>
                        </span>
                        <div>
                          <p className="text-xs text-foreground-400">电子邮箱</p>
                          <p className="text-sm font-medium text-foreground-800">hello@niuniu.tv</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                          <i className="ri-wechat-line"></i>
                        </span>
                        <div>
                          <p className="text-xs text-foreground-400">微信公众号</p>
                          <p className="text-sm font-medium text-foreground-800">牛牛影视</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                          <i className="ri-timer-line"></i>
                        </span>
                        <div>
                          <p className="text-xs text-foreground-400">工作时间</p>
                          <p className="text-sm font-medium text-foreground-800">周一至周五 9:00 - 18:00</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 bg-background-100 rounded-xl p-6 border border-background-200/70">
                    <h3 className="text-base font-bold text-foreground-950 mb-1 font-heading">发送消息</h3>
                    <p className="text-xs text-foreground-500 mb-5">我们将尽快回复您的留言</p>
                    <form
                      onSubmit={handleSubmit}
                      data-readdy-form
                      className="space-y-4"
                    >
                      <div>
                        <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="about-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="about-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1">
                          留言内容 <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          id="about-message"
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      {formStatus === 'error' && formError && (
                        <p className="text-xs text-primary-600 bg-primary-50 px-3 py-2 rounded-lg">{formError}</p>
                      )}
                      {formStatus === 'success' && (
                        <p className="text-xs text-accent-700 bg-accent-50 px-3 py-2 rounded-lg">
                          消息已发送成功！我们会尽快回复您，感谢您的留言。
                        </p>
                      )}
                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="w-full py-2.5 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 disabled:opacity-60 transition-all text-sm whitespace-nowrap cursor-pointer"
                      >
                        {formStatus === 'loading' ? '发送中...' : '发送消息'}
                      </button>
                    </form>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}